import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, Minus, Plus, Truck, ShieldCheck, RotateCcw, ChevronRight, Check, Play, ExternalLink } from 'lucide-react';
import { fetchProduct, fetchProducts } from '../lib/api';
import ProductCard from '../components/ProductCard';
import ProductPolicies from '../components/ProductPolicies';
import NotifyMe from '../components/NotifyMe';
import PhotoTestimonials from '../components/PhotoTestimonials';
import LookBook from '../components/LookBook';
import CollectionInfo from '../components/CollectionInfo';
import Reveal from '../components/Reveal';
import { SkeletonPDP } from '../components/motion/Skeleton';
import { flyToCart } from '../lib/flyToCart';
import LengthGuide from '../components/LengthGuide';
import ShadeCompare from '../components/ShadeCompare';
import RecentlyViewed from '../components/RecentlyViewed';
import { addRecentlyViewed } from '../lib/recentlyViewed';
import { useCart } from '../context/CartContext';
import { swatchFor } from '../lib/colors';
import { activeSale, withSale } from '../lib/sale';
import { getTestimonials } from '../mock';
import Magnetic from '../components/motion/Magnetic';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [length, setLength] = useState();
  const [color, setColor] = useState();
  const [customColor, setCustomColor] = useState('');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [showBar, setShowBar] = useState(false);
  const [hoverColor, setHoverColor] = useState(null);
  const [colorTouched, setColorTouched] = useState(false);
  const [density, setDensity] = useState(null);
  const [mediaHover, setMediaHover] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 700);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetchProduct(id)
      .then((p) => {
        setProduct(p);
        if (p) {
          addRecentlyViewed(p);
          setLength(p.lengths?.[0]);
          setColor(p.colors?.[0]);
          setDensity(p.densities?.[0] || null);
          setColorTouched(false);
          setCustomColor('');
          setQty(1);
          setActiveImg(0);
          fetchProducts(p.category)
            .then((list) => setRelated(list.filter((x) => x.id !== p.id && !x.coming_soon).slice(0, 4)))
            .catch(() => {});
        }
        setLoading(false);
      })
      .catch(() => { setProduct(null); setLoading(false); });
  }, [id]);

  if (loading) {
    return <div className="pt-10"><SkeletonPDP /></div>;
  }

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-32 text-center">
        <h2 className="font-display text-4xl text-[#1a1a1a] mb-4">Product not found</h2>
        <Link to="/shop" className="btn-gold inline-block px-8 py-3.5"><span>Back to Shop</span></Link>
      </div>
    );
  }

  const gallery = product.gallery?.length ? product.gallery : [product.image];
  // Only swap the gallery to a shade image once the shopper hovers or actively picks a color —
  // the default selection keeps the full product gallery (model + mannequin front/back) visible.
  const displayColor = hoverColor || (colorTouched ? color : null);
  const colorSet = product.color_galleries?.[displayColor];
  const displayGallery = (colorSet && colorSet.length)
    ? colorSet
    : (product.color_images?.[displayColor] ? [product.color_images[displayColor]] : gallery);
  const imgIdx = Math.min(activeImg, displayGallery.length - 1);
  const fitClass = product.image_fit === 'contain' ? 'object-contain' : 'object-cover';
  const productVideos = product.videos || (product.video ? [product.video] : []);
  const videoMedia = productVideos.map((v) => ({ type: 'video', src: v, poster: product.image }));
  const imageMedia = displayGallery.map((s) => ({ type: 'image', src: s }));
  const media = productVideos.length
    ? (product.image_first ? [...imageMedia, ...videoMedia] : [...videoMedia, ...imageMedia])
    : imageMedia;
  const mIdx = Math.min(activeImg, media.length - 1);
  // While hovering a color swatch, preview that shade's image as the main media (works even when a video is the default hero)
  const hoverColorImg = hoverColor
    ? (product.color_galleries?.[hoverColor]?.[0] || product.color_images?.[hoverColor])
    : null;
  const activeMedia = hoverColorImg ? { type: 'image', src: hoverColorImg } : media[mIdx];
  // Colored hair products (bundles & wigs): hovering the main image previews the model wearing it.
  const hoverModelImg = ['bundles', 'wigs'].includes(product.category) ? (product.gallery?.[1] || null) : null;
  const showHoverModel = !!hoverModelImg && mediaHover && !hoverColorImg
    && activeMedia.src !== hoverModelImg;
  const activePrices = (density && product.density_prices?.[density]) || product.price_by_length;
  const basePrice = activePrices?.[length] ?? product.price;
  const unitPrice = basePrice + (product.custom_color && customColor.trim() ? 10 : 0);
  const comingSoon = product.coming_soon;
  const sale = activeSale(product);

  const handleAdd = () => {
    const finalColor = customColor.trim() ? `Custom – ${customColor.trim()}` : color;
    flyToCart(document.querySelector('[data-testid="pdp-main-image"]'), product.image);
    addItem(product, { length, color: finalColor, density, quantity: qty, price: unitPrice });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5 flex items-center gap-2 font-body text-xs text-[#6b6459]">
        <Link to="/" className="hover:text-[#a8823c]">Home</Link><ChevronRight size={13} />
        <Link to="/shop" className="hover:text-[#a8823c]">Shop</Link><ChevronRight size={13} />
        <Link to={`/shop/${product.category}`} className="hover:text-[#a8823c] capitalize">{product.category.replace('-', ' ')}</Link><ChevronRight size={13} />
        <span className="text-[#1a1a1a] truncate">{product.name}</span>
      </div>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-20 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-start gap-4">
          {media.length > 1 && (
            <div className="flex sm:flex-col gap-3">
              {media.map((m, i) => (
                <button key={`${m.type}-${m.src}`} onClick={() => setActiveImg(i)}
                  className={`relative w-20 h-24 overflow-hidden border ${mIdx === i ? 'border-[#c6a15b]' : 'border-transparent'}`}>
                  <img src={m.type === 'video' ? m.poster : m.src} alt="" className={`w-full h-full ${fitClass}`} />
                  {m.type === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center bg-[#0b0b0b]/25">
                      <Play size={16} className="text-white fill-white" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 bg-[#ece5d8] aspect-[3/4] overflow-hidden relative"
            onMouseEnter={() => setMediaHover(true)} onMouseLeave={() => setMediaHover(false)}>
            {activeMedia.type === 'video' ? (
              <video src={activeMedia.src} poster={activeMedia.poster} data-testid="pdp-main-image"
                autoPlay muted loop playsInline preload="metadata" className={`w-full h-full ${fitClass}`} />
            ) : (
              <img src={activeMedia.src} alt={product.name} data-testid="pdp-main-image" className={`w-full h-full ${fitClass} transition-opacity duration-300`} />
            )}
            {hoverModelImg && (
              <img src={hoverModelImg} alt="" aria-hidden="true" data-testid="pdp-hover-model"
                className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none transition-opacity duration-500 ${showHoverModel ? 'opacity-100' : 'opacity-0'}`} />
            )}
          </div>
        </div>

        {/* Info */}
        <div className="lg:pt-4">
          {product.bestseller && <span className="inline-block bg-[#0b0b0b] text-[#e7d3a1] overline text-[0.58rem] px-3 py-1.5 mb-4">Bestseller</span>}
          <h1 className="font-display text-4xl sm:text-5xl text-[#1a1a1a] leading-tight">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={15} className={i < Math.round(product.rating) ? 'fill-[#c6a15b] text-[#c6a15b]' : 'text-[#d8cdb8]'} />)}</div>
            <span className="font-body text-sm text-[#6b6459]">{product.rating} ({product.reviews} reviews)</span>
          </div>
          <p className="font-display text-3xl text-[#a8823c] mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="pdp-price">
            {sale ? (
              <>
                <span className="text-[#a39c8e] line-through text-2xl">${unitPrice.toFixed(2)}</span>
                <span className="text-emerald-600 font-semibold">${withSale(unitPrice, sale).toFixed(2)}</span>
                <span className="font-body text-[0.6rem] tracking-widest uppercase bg-emerald-600 text-white px-2 py-1 rounded-sm not-italic">Save {sale.percent}%</span>
              </>
            ) : (
              <span>${unitPrice.toFixed(2)}</span>
            )}
            {product.price_by_length && <span className="font-body text-sm text-[#6b6459]">· {length}{density ? ` · ${density} density` : ''}</span>}
          </p>
          {sale && <p className="font-body text-xs text-emerald-700 mt-1" data-testid="pdp-sale-note">Sale price applied automatically at checkout — {sale.percent}% off through Sep 12, 11:59 PM ET</p>}
          <p className="font-body text-[#4a463d] leading-relaxed mt-5">{product.description}</p>
          {product.densities?.length > 1 && (
            <div className="mt-6" data-testid="density-selector">
              <p className="overline text-[#1a1a1a] text-[0.65rem] mb-3">Density — <span className="text-[#a8823c]">{density}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.densities.map((d) => (
                  <button key={d} type="button" onClick={() => setDensity(d)} data-testid={`density-option-${d}`}
                    className={`px-4 py-2.5 border font-body text-sm transition-colors ${density === d ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#4a463d] hover:border-[#0b0b0b]'}`}>
                    {d === '250%' ? '250% — Full & Thick' : '180% — Affordable Luxury'}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.density_prices ? (
            <div className="mt-6 border border-[#e0d6c3] rounded-md overflow-hidden" data-testid="density-price-table">
              <div className="bg-[#f2ece0] px-4 py-2.5 grid grid-cols-3 overline text-[0.6rem] text-[#6b6459]">
                <span>Length</span>
                <span className={`text-center ${density === '250%' ? 'text-[#a8823c]' : ''}`}>250% Density</span>
                <span className={`text-right ${density === '180%' ? 'text-[#a8823c]' : ''}`}>180% Density</span>
              </div>
              <div className="divide-y divide-[#eee5d4]">
                {product.lengths.map((ln) => (
                  <div key={ln} className="grid grid-cols-3 items-stretch">
                    <button type="button" onClick={() => setLength(ln)} data-testid={`price-row-${ln}`}
                      className={`px-4 py-2 text-left font-body text-sm transition-colors ${length === ln ? 'bg-[#f2ece0] text-[#1a1a1a] font-semibold' : 'text-[#4a463d] hover:bg-[#f7f3ec]'}`}>
                      {ln}
                    </button>
                    {['250%', '180%'].map((d) => (
                      <button key={d} type="button" onClick={() => { setLength(ln); setDensity(d); }}
                        data-testid={`price-cell-${ln}-${d}`}
                        className={`px-4 py-2 font-body text-sm tracking-wide transition-colors ${d === '250%' ? 'text-center' : 'text-right'} ${length === ln && density === d ? 'bg-[#0b0b0b] text-[#e7d3a1]' : 'text-[#4a463d] hover:bg-[#f7f3ec]'}`}>
                        {sale ? (
                          <span className="flex flex-col items-center leading-tight">
                            <span className="text-[0.6rem] text-[#a39c8e] line-through">${(product.density_prices[d]?.[ln] ?? 0).toFixed(2)}</span>
                            <span className="text-emerald-500 font-semibold">${withSale(product.density_prices[d]?.[ln] ?? 0, sale).toFixed(2)}</span>
                          </span>
                        ) : `$${(product.density_prices[d]?.[ln] ?? 0).toFixed(2)}`}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : product.price_by_length && (
            <div className="mt-6 border border-[#e0d6c3] rounded-md overflow-hidden" data-testid="price-by-length-table">
              <div className="bg-[#f2ece0] px-4 py-2.5 flex justify-between overline text-[0.6rem] text-[#6b6459]">
                <span>Length</span><span>Price</span>
              </div>
              <div className="divide-y divide-[#eee5d4]">
                {Object.entries(product.price_by_length).map(([ln, pr]) => (
                  <button key={ln} onClick={() => setLength(ln)} type="button"
                    data-testid={`price-row-${ln}`}
                    className={`w-full flex justify-between items-center px-4 py-2 font-body text-sm transition-colors ${length === ln ? 'bg-[#0b0b0b] text-[#e7d3a1]' : 'text-[#4a463d] hover:bg-[#f7f3ec]'}`}>
                    <span>{ln}</span>
                    {sale ? (
                      <span className="tracking-wide flex items-center gap-2">
                        <span className="text-[#a39c8e] line-through">${pr.toFixed(2)}</span>
                        <span className="text-emerald-500 font-semibold">${withSale(pr, sale).toFixed(2)}</span>
                      </span>
                    ) : (
                      <span className="tracking-wide">${pr.toFixed(2)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          {product.features?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-2 font-body text-sm text-[#4a463d]">
                  <Check size={15} className="mt-0.5 text-[#a8823c] shrink-0" /> {f}
                </li>
              ))}
            </ul>
          )}
          {product.whyLove?.length > 0 && (
            <div className="mt-7">
              <h3 className="font-display text-2xl text-[#1a1a1a] mb-3">Why You’ll Love It</h3>
              <ul className="space-y-2.5">
                {product.whyLove.map((w) => {
                  const [lead, ...rest] = w.split(':');
                  const detail = rest.join(':');
                  return (
                    <li key={w} className="flex items-start gap-2 font-body text-sm text-[#4a463d]">
                      <Check size={15} className="mt-0.5 text-[#a8823c] shrink-0" />
                      <span>{detail ? <><span className="font-medium text-[#1a1a1a]">{lead}:</span>{detail}</> : w}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {product.perfectFor && (
            <div className="mt-7">
              <h3 className="font-display text-2xl text-[#1a1a1a] mb-2">Perfect For</h3>
              <p className="font-body text-sm text-[#4a463d] leading-relaxed">{product.perfectFor}</p>
            </div>
          )}
          <div className="gold-line my-8" />

          {/* Length */}
          {product.lengths?.length > 0 && (
            <div className="mb-6">
              <p className="overline text-[#1a1a1a] text-[0.65rem] mb-3">Length — <span className="text-[#a8823c]">{length}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.lengths.map((l) => (
                  <button key={l} onClick={() => setLength(l)}
                    className={`min-w-[54px] px-4 py-2.5 font-body text-sm border transition-colors ${length === l ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#4a463d] hover:border-[#c6a15b]'}`}>{l}</button>
                ))}
              </div>
            </div>
          )}

          {/* Length Guide (inches to your look) — bundles, wigs & frontals */}
          {product.lengths?.length > 0 && ['bundles', 'wigs', 'frontals'].includes(product.category) && (
            <LengthGuide selected={length} lengths={product.lengths} onSelect={setLength} />
          )}

          {/* Color */}
          {product.colors?.length > 0 && (
            <div className="mb-8">
              <p className="overline text-[#1a1a1a] text-[0.65rem] mb-3">Color — <span className="text-[#a8823c]">{color}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => {
                  const previewImg = product.color_galleries?.[c]?.[0] || product.color_images?.[c];
                  return (
                  <div key={c} className="relative">
                    <button onClick={() => { setColor(c); setColorTouched(true); setActiveImg(0); }} data-testid={`color-option-${c}`}
                      onMouseEnter={() => setHoverColor(c)} onMouseLeave={() => setHoverColor(null)}
                      className={`flex items-center gap-2 pl-2 pr-4 py-2 font-body text-sm border transition-colors ${color === c ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#4a463d] hover:border-[#c6a15b]'}`}>
                      <span className="w-4 h-4 rounded-full border border-black/20 shrink-0" style={{ background: swatchFor(c) }} />
                      {c}
                    </button>
                    {previewImg && hoverColor === c && (
                      <div
                        data-testid={`color-preview-${c}`}
                        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-50 w-40 rounded-lg overflow-hidden bg-white shadow-2xl ring-1 ring-[#c6a15b]/50 animate-[fadeIn_0.18s_ease-out]"
                      >
                        <div className="aspect-square bg-[#ece5d8] overflow-hidden">
                          <img src={previewImg} alt={c} className="w-full h-full object-cover" />
                        </div>
                        <p className="text-center font-body text-[0.7rem] text-[#1a1a1a] py-1.5 px-2 truncate">{c}</p>
                        <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 ring-1 ring-[#c6a15b]/50" />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              {(product.color_images || product.color_galleries) && (
                <ShadeCompare
                  colors={product.colors}
                  imageFor={(c) => product.color_galleries?.[c]?.[0] || product.color_images?.[c]}
                  onChoose={(c) => { setColor(c); setColorTouched(true); setActiveImg(0); }}
                  lengths={product.lengths || []}
                  selectedLength={length}
                />
              )}
            </div>
          )}

          {/* Custom Color (bundles) */}
          {product.custom_color && (
            <div className="mb-8" data-testid="custom-color-box">
              <p className="overline text-[#1a1a1a] text-[0.65rem] mb-2">Custom Color <span className="text-[#6b6459] normal-case tracking-normal">(optional · +$10)</span></p>
              <p className="font-body text-xs text-[#6b6459] mb-3">Custom colors are available for a $10 custom color service charge — please specify the exact color you'd like. Custom orders are made just for you and may require additional processing time.</p>
              <textarea
                value={customColor}
                onChange={(e) => setCustomColor(e.target.value.slice(0, 500))}
                data-testid="custom-color-input"
                rows={2}
                placeholder="e.g. Honey blonde ombre with dark roots"
                className="field-luxe w-full bg-white border border-[#d8cdb8] px-4 py-3 font-body text-sm text-[#1a1a1a] resize-none" />
              <p className="font-body text-[0.65rem] text-[#9c9587] text-right mt-1">{customColor.length} / 500</p>
            </div>
          )}

          {/* Qty + Add */}
          {comingSoon ? (
            <div className="border border-[#c6a15b]/40 bg-[#f2ece0] px-6 py-6 text-center" data-testid="coming-soon-notice">
              <p className="overline text-[#a8823c] mb-1">Coming Soon</p>
              <p className="font-body text-sm text-[#6b6459] mb-4">This style is dropping soon. Enter your email and we'll alert you the moment it launches.</p>
              <NotifyMe productId={product.id} variant="pdp" />
            </div>
          ) : product.external_url ? (
          <div>
            <Magnetic className="block">
              <a href={product.external_url} target="_blank" rel="noreferrer" data-testid="buy-from-website-btn"
                className="btn-gold btn-shimmer btn-attention w-full py-4 flex items-center justify-center gap-2">
                <span className="flex items-center gap-2">Buy From Website <ExternalLink size={16} /></span>
              </a>
            </Magnetic>
            <p className="font-body text-xs text-[#9c9587] text-center mt-3">Fulfilled by our trusted partner Bask &amp; Lather Co. — opens in a new tab</p>
            <p className="font-body text-[0.68rem] italic text-[#a8a091] text-center mt-2" data-testid="bl-price-disclaimer">*Listed price may differ from Bask &amp; Lather website. Check Bask &amp; Lather website for current pricing.</p>
          </div>
          ) : (
          <>
          <div className="flex items-stretch gap-4">
            <div className="flex items-center border border-[#d8cdb8]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 text-[#6b6459] hover:text-[#0b0b0b]"><Minus size={15} /></button>
              <span className="px-4 font-body">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 text-[#6b6459] hover:text-[#0b0b0b]"><Plus size={15} /></button>
            </div>
            <Magnetic className="flex-1">
              <button onClick={handleAdd} data-testid="add-to-bag-btn" className="btn-gold btn-shimmer btn-attention w-full py-4 flex items-center justify-center gap-2">
                <span className="flex items-center gap-2">{added ? <><Check size={16} /> Added To Bag</> : 'Add To Bag'}</span>
              </button>
            </Magnetic>
          </div>
          <button onClick={() => { const finalColor = customColor.trim() ? `Custom – ${customColor.trim()}` : color; addItem(product, { length, color: finalColor, density, quantity: qty, price: unitPrice }); navigate('/cart'); }}
            className="w-full mt-3 py-4 border border-[#0b0b0b] text-[#0b0b0b] overline text-xs hover:bg-[#0b0b0b] hover:text-[#e7d3a1] transition-colors">
            Buy It Now
          </button>
          </>
          )}

          {/* Perks */}
          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-[#e6ddcd]">
            {[{ icon: Truck, t: 'Fast Shipping' }, { icon: ShieldCheck, t: 'Quality Assured' }, { icon: RotateCcw, t: 'Easy Support' }].map((x) => (
              <div key={x.t} className="text-center">
                <x.icon size={22} className="text-[#c6a15b] mx-auto mb-2" />
                <p className="font-body text-xs text-[#6b6459]">{x.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collection story diagram + comparison chart (Imperial Luxe / Silk Dynasty) */}
      {(product.collection === 'imperial-luxe' || product.collection === 'silk-dynasty') && (
        <CollectionInfo collection={product.collection} />
      )}

      {/* Return & Refund + Shipping policies (all products) */}
      <ProductPolicies />

      {/* Customer photo reviews for this category */}
      <PhotoTestimonials items={getTestimonials(product.category, product.id, comingSoon)}
        subtitle="Verified Buyers"
        title={comingSoon ? 'Queens Who Trust Us' : 'Loved by Our Queens'}
        dark={false}
        variant="cards"
        showImages={false} />

      {/* Look Book (only products that have one, e.g. the 38" braid) */}
      {product.lookbook?.length > 0 && <LookBook images={product.lookbook} />}

      {/* Recently viewed strip */}
      <RecentlyViewed excludeId={product.id} />

      {/* Related (hidden on frontal & closure pages) */}
      {product.category !== 'frontals' && related.length > 0 && (
        <section className="bg-[#141311] py-20">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
            <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl text-[#f7f3ec] text-center mb-12">You May Also Love</h2>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                <div className="[&_h3]:text-[#f7f3ec] [&_.font-body]:text-[#cfc7b8]">
                  <ProductCard product={p} index={i} />
                </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sticky slide-up buy bar */}
      {!comingSoon && (
        <div
          data-testid="sticky-buy-bar"
          className={`fixed bottom-0 left-0 right-0 z-40 bg-[#0b0b0b]/95 backdrop-blur border-t border-[#c6a15b]/30 transition-transform duration-500 ${showBar ? 'translate-y-0' : 'translate-y-full'}`}
        >
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
            <img src={product.image} alt="" className="w-12 h-14 object-cover hidden sm:block" />
            <div className="min-w-0 flex-1">
              <p className="font-display text-base sm:text-lg text-[#f7f3ec] truncate">{product.name}</p>
              <p className="font-body text-sm text-[#e7d3a1]">
                {product.price_by_length ? `From $${unitPrice.toFixed(2)}` : `$${unitPrice.toFixed(2)}`}{length ? ` · ${length}` : ''}
              </p>
            </div>
            <Magnetic>
              {product.external_url ? (
                <a href={product.external_url} target="_blank" rel="noreferrer" data-testid="sticky-buy-website-btn" className="btn-gold px-6 sm:px-10 py-3 whitespace-nowrap flex items-center gap-2">
                  Buy From Website <ExternalLink size={14} />
                </a>
              ) : (
                <button onClick={handleAdd} data-testid="sticky-add-btn" className="btn-gold px-6 sm:px-10 py-3 whitespace-nowrap">
                  <span className="flex items-center gap-2">{added ? <><Check size={15} /> Added</> : 'Add To Bag'}</span>
                </button>
              )}
            </Magnetic>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
